import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack,
    Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import dayjs from "dayjs";

import ComboBox from "../../component/common/ComboBox";
import { api } from "../../util/api";
import { notify } from "../../util/toast";
import { useMyPermissions } from "../../util/useMyPermissions";

/** 발송 범위 — 백엔드 AdminPushSendRequest.Scope 와 값이 일치해야 한다. */
const SCOPES = [
    { value: "PICK", label: "선택한 직원" },
    { value: "ALL", label: "재직 직원 전체" },
];

/**
 * 관리자 전용 — 휴대폰 알림 현황과 직접 발송.
 *
 * <h3>이 화면이 실제로 쓰이는 순간</h3>
 * <ul>
 *   <li><b>"알림이 안 와요" 문의</b> — 미등록 명단을 보면 애초에 등록을 안 한 것인지 즉시 갈린다.
 *       이게 문의의 대부분이다.</li>
 *   <li><b>"알림이 두 번 와요"</b> — 기기 목록에서 같은 폰에 브라우저 두 개가 등록된 게 보인다.</li>
 *   <li><b>"어제까진 왔는데"</b> — 마지막 수신 시각이 오래됐으면 구독이 조용히 만료된 것이다.</li>
 *   <li><b>시스템 점검 안내</b> — 메일보다 즉시성이 필요할 때 직접 발송한다.</li>
 * </ul>
 *
 * <p>권한은 백엔드가 {@code GLOBAL_ADMIN} 으로 강제한다. 여기서도 관리자가 아니면 화면을 감춘다 —
 * 백엔드가 막으니 안 감춰도 데이터가 새지는 않지만, 눌러도 실패하는 버튼을 보여줄 이유가 없다.
 */
export default function PushAdminPage() {
    const { isAdmin, loading: permLoading } = useMyPermissions();

    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);

    const [scope, setScope] = useState("PICK");
    const [picked, setPicked] = useState([]);      // 사번 배열
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            setOverview(await api.get("/api/admin/push/overview"));
        } catch (e) {
            notify(e?.message || "현황을 불러오지 못했습니다.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

    /**
     * 수신자 선택 후보 — 등록자 + 미등록자 전원.
     *
     * <p>미등록자도 후보에 넣는 이유 — 알림은 <b>종 아이콘에도</b> 남는다. 폰을 등록하지 않은
     * 사람에게 보내도 헛일이 아니라, PC 로 로그인하면 보인다.
     */
    const candidates = useMemo(() => {
        if (!overview) return [];
        const registered = (overview.registered ?? []).map(r => ({
            code: r.employeeCode, name: r.name, deviceCount: r.deviceCount,
        }));
        const unregistered = (overview.notRegistered ?? []).map(u => ({
            code: u.employeeCode, name: u.name, deviceCount: 0,
        }));
        return [...registered, ...unregistered]
            .sort((a, b) => (a.name || "").localeCompare(b.name || "", "ko"));
    }, [overview]);

    const togglePicked = (code) => {
        setPicked(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
    };

    const recipientCount = scope === "ALL"
        ? (overview?.totalActiveEmployees ?? 0)
        : picked.length;

    const canSend = !!title.trim() && recipientCount > 0 && !sending;

    /**
     * 발송 결과 안내.
     *
     * 발송이 성공하면 서버 로그에는 아무것도 남지 않는다(푸시 서버가 201 을 주면 조용히 끝난다).
     * 그래서 "발송했습니다" 만 띄우면, 폰에 알림이 안 왔을 때 관리자가 원인을 알 수 없다 —
     * 실제로 그 상태에서 "강제 발송이 동작을 안 한다" 로 보였다.
     *
     * 알림은 폰 미등록자에게도 종 아이콘으로는 간다. 그래서 두 숫자를 나눠 보여준다.
     * 특히 등록자가 0명이면 폰에는 아무것도 뜨지 않는 게 정상이므로 경고로 알린다 —
     * 그게 "발송 실패" 로 오해되는 지점이다.
     */
    const notifySendResult = (sent, withDevices) => {
        if (withDevices === 0) {
            notify(`${sent}명에게 발송했습니다.\n`
                + `폰에 등록된 기기가 없어 종 아이콘에만 표시됩니다.`, "warning", { autoClose: 6000 });
            return;
        }
        if (withDevices === sent) {
            notify(`${sent}명에게 발송했습니다 — 전원 폰으로 받습니다.`, "success");
            return;
        }
        notify(`${sent}명에게 발송했습니다.\n`
            + `폰으로 받는 사람 ${withDevices}명, `
            + `나머지 ${sent - withDevices}명은 종 아이콘에만 표시됩니다.`, "success", { autoClose: 6000 });
    };

    const send = async () => {
        setSending(true);
        try {
            const body = {
                scope,
                employeeCodes: scope === "PICK" ? picked : undefined,
                title: title.trim(),
                message: message.trim() || undefined,
            };
            const res = await api.post("/api/admin/push/notifications", body);
            notifySendResult(res?.sent ?? 0, res?.withDevices ?? 0);
            setTitle("");
            setMessage("");
            setPicked([]);
            load();
        } catch (e) {
            notify(e?.message || "발송에 실패했습니다.", "error");
        } finally {
            setSending(false);
        }
    };

    if (permLoading) {
        return <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={22} /></Box>;
    }
    if (!isAdmin) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">관리자만 접근할 수 있는 화면입니다.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                <NotificationsActiveRoundedIcon sx={{ color: "#EA292C" }} />
                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>휴대폰 알림 관리</Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading}>
                    새로고침
                </Button>
            </Stack>
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2.5 }}>
                직원별 등록 현황을 확인하고, 필요할 때 직접 알림을 보냅니다.
            </Typography>

            {loading && <Box sx={{ py: 4, textAlign: "center" }}><CircularProgress size={22} /></Box>}

            {!loading && overview && (
                <Stack spacing={2.5}>
                    <SummaryCards overview={overview} />
                    <SendPanel
                        scope={scope} setScope={setScope}
                        candidates={candidates} picked={picked} togglePicked={togglePicked}
                        title={title} setTitle={setTitle}
                        message={message} setMessage={setMessage}
                        recipientCount={recipientCount}
                        canSend={canSend} sending={sending} onSend={send}
                    />
                    <RegisteredTable rows={overview.registered ?? []} />
                    <UnregisteredTable rows={overview.notRegistered ?? []} />
                </Stack>
            )}
        </Box>
    );
}

/* ─── 요약 ─────────────────────────────────────────────── */

function SummaryCards({ overview }) {
    const rate = overview.totalActiveEmployees > 0
        ? Math.round((overview.registeredEmployees / overview.totalActiveEmployees) * 100)
        : 0;

    return (
        <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
            <Metric label="재직 직원" value={`${overview.totalActiveEmployees}명`} />
            <Metric label="알림 등록" value={`${overview.registeredEmployees}명`} sub={`${rate}%`} />
            <Metric label="등록 기기" value={`${overview.totalDevices}대`} />
            {/* 구독은 무통보로 만료된다 — 이 숫자가 "조용히 끊긴 사람" 을 찾는 유일한 조기 신호다. */}
            <Metric
                label="30일 이상 미수신"
                value={`${overview.staleDevices}대`}
                tone={overview.staleDevices > 0 ? "warn" : "normal"}
            />
        </Stack>
    );
}

function Metric({ label, value, sub, tone = "normal" }) {
    const color = tone === "warn" ? "#b45309" : "#0f172a";
    const bg = tone === "warn" ? "#fffbeb" : "#f8fafc";
    return (
        <Paper elevation={0} sx={{ px: 2, py: 1.5, borderRadius: 2, border: "1px solid #e2e8f0",
                                   bgcolor: bg, minWidth: 150, flex: "1 1 150px" }}>
            <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{label}</Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.8}>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color }}>{value}</Typography>
                {sub && <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{sub}</Typography>}
            </Stack>
        </Paper>
    );
}

/* ─── 발송 ─────────────────────────────────────────────── */

function SendPanel({
    scope, setScope, candidates, picked, togglePicked,
    title, setTitle, message, setMessage, recipientCount, canSend, sending, onSend,
}) {
    return (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>직접 발송</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
                받는 사람의 종 아이콘과 휴대폰에 함께 나갑니다. 보낸 사람은 "시스템" 으로 표시되지만
                기록에는 남습니다.
            </Typography>

            <Stack spacing={2}>
                <Box sx={{ maxWidth: 240 }}>
                    <FieldLabel>받는 범위</FieldLabel>
                    <ComboBox options={SCOPES} value={scope} onChange={setScope} />
                </Box>

                {scope === "PICK" && (
                    <Box>
                        <FieldLabel>
                            받는 사람 {picked.length > 0 && `(${picked.length}명 선택)`}
                        </FieldLabel>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, maxHeight: 160,
                                   overflowY: "auto", p: 1, border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
                            {candidates.map(c => (
                                <Chip
                                    key={c.code}
                                    size="small"
                                    label={c.deviceCount > 0 ? `${c.name}` : `${c.name} (미등록)`}
                                    icon={c.deviceCount > 0 ? <PhoneIphoneRoundedIcon /> : undefined}
                                    onClick={() => togglePicked(c.code)}
                                    color={picked.includes(c.code) ? "primary" : "default"}
                                    variant={picked.includes(c.code) ? "filled" : "outlined"}
                                    sx={{ fontSize: 11.5 }}
                                />
                            ))}
                        </Box>
                        <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.5 }}>
                            미등록 직원에게 보내도 헛일이 아닙니다 — 종 아이콘에는 남아 PC 로 보면 보입니다.
                        </Typography>
                    </Box>
                )}

                <Box>
                    <FieldLabel>제목 (필수)</FieldLabel>
                    <TextField fullWidth size="small" value={title}
                               onChange={(e) => setTitle(e.target.value)}
                               placeholder="예) 시스템 점검 안내" inputProps={{ maxLength: 200 }} />
                </Box>

                <Box>
                    <FieldLabel>본문 (선택)</FieldLabel>
                    <TextField fullWidth size="small" multiline minRows={2} value={message}
                               onChange={(e) => setMessage(e.target.value)}
                               placeholder="예) 오늘 22시부터 30분간 접속이 제한됩니다." />
                </Box>

                <NotificationPreview title={title} message={message} />

                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Button variant="contained" startIcon={<SendRoundedIcon />}
                            onClick={onSend} disabled={!canSend}
                            sx={{ bgcolor: "#EA292C", "&:hover": { bgcolor: "#c81f22" } }}>
                        {sending ? "발송 중…" : `${recipientCount}명에게 발송`}
                    </Button>
                    <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>
                        전사 알림을 자주 보내면 직원들이 브라우저 알림을 끄고, 그러면 결재 알림도 안 갑니다.
                    </Typography>
                </Stack>
            </Stack>
        </Paper>
    );
}

/**
 * 잠금화면 미리보기.
 *
 * <p>관리자는 자기 폰에 어떻게 뜰지 모른 채 문구를 쓴다. 특히 <b>"온앤미" 출처 줄은 브라우저가
 * 붙이는 것</b>이어서 제목에 "온앤미" 를 또 넣으면 두 번 나온다 — 그 실수를 쓰는 중에 보게 한다.
 */
function NotificationPreview({ title, message }) {
    const shown = title.trim() || "(제목을 입력하세요)";
    return (
        <Box>
            <FieldLabel>휴대폰에 이렇게 뜹니다</FieldLabel>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#0f172a", maxWidth: 380 }}>
                <Box sx={{ bgcolor: "rgba(255,255,255,0.96)", borderRadius: 1.5, px: 1.5, py: 1.2 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a",
                                      whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {shown}
                    </Typography>
                    {message.trim() && (
                        <Typography sx={{ fontSize: 12, color: "#334155", mt: 0.3,
                                          whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {message.trim()}
                        </Typography>
                    )}
                    {/* 브라우저가 붙이는 출처 줄 — 우리가 없앨 수 없다 */}
                    <Typography sx={{ fontSize: 10.5, color: "#94a3b8", mt: 0.5 }}>온앤미</Typography>
                </Box>
            </Paper>
        </Box>
    );
}

/* ─── 현황 표 ───────────────────────────────────────────── */

function RegisteredTable({ rows }) {
    const staleBefore = dayjs().subtract(30, "day");

    return (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
                등록한 직원 {rows.length}명
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
                기기가 2대 이상이면 알림이 여러 번 갈 수 있습니다. "알림이 두 번 와요" 문의는 여기를 봅니다.
            </Typography>

            {rows.length === 0 && <Empty>아직 등록한 직원이 없습니다.</Empty>}

            {rows.length > 0 && (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <Th>직원</Th><Th>기기</Th><Th>등록 기기</Th><Th>최근 수신</Th>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(r => {
                            const stale = !r.lastSuccessAt || dayjs(r.lastSuccessAt).isBefore(staleBefore);
                            return (
                                <TableRow key={r.employeeCode} hover>
                                    <Td>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{r.name}</Typography>
                                        <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>
                                            {r.employeeCode}
                                        </Typography>
                                    </Td>
                                    <Td>
                                        <Chip size="small" label={`${r.deviceCount}대`}
                                              color={r.deviceCount > 1 ? "warning" : "default"}
                                              sx={{ height: 20, fontSize: 11 }} />
                                    </Td>
                                    <Td>
                                        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                                            {(r.devices ?? []).join(" · ")}
                                        </Typography>
                                    </Td>
                                    <Td>
                                        <Typography sx={{ fontSize: 11.5,
                                                          color: stale ? "#b45309" : "text.secondary" }}>
                                            {r.lastSuccessAt
                                                ? dayjs(r.lastSuccessAt).format("YYYY-MM-DD HH:mm")
                                                : "아직 없음"}
                                        </Typography>
                                        {stale && (
                                            <Typography sx={{ fontSize: 10, color: "#b45309" }}>
                                                구독이 만료됐을 수 있습니다 — 재등록 안내 필요
                                            </Typography>
                                        )}
                                    </Td>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </Paper>
    );
}

function UnregisteredTable({ rows }) {
    return (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
                미등록 직원 {rows.length}명
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 1.5 }}>
                이 분들은 폰으로 알림을 받지 못합니다. 종 아이콘 → 실시간 알림에서 QR 로 등록하도록 안내하세요.
            </Typography>

            {rows.length === 0
                ? <Empty>전원 등록 완료입니다.</Empty>
                : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
                        {rows.map(u => (
                            <Chip key={u.employeeCode} size="small" variant="outlined"
                                  label={`${u.name} (${u.employeeCode})`} sx={{ fontSize: 11.5 }} />
                        ))}
                    </Box>
                )}
        </Paper>
    );
}

/* ─── 소품 ─────────────────────────────────────────────── */

function FieldLabel({ children }) {
    return (
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.6 }}>
            {children}
        </Typography>
    );
}

function Th({ children }) {
    return <TableCell sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary" }}>{children}</TableCell>;
}

function Td({ children }) {
    return <TableCell sx={{ py: 1 }}>{children}</TableCell>;
}

function Empty({ children }) {
    return (
        <>
            <Divider sx={{ mb: 1.5 }} />
            <Typography sx={{ fontSize: 12.5, color: "text.disabled", textAlign: "center", py: 2 }}>
                {children}
            </Typography>
        </>
    );
}
